import "./layout.scss";
import { Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode } from "react";
import {
    DollarSignIcon,
    RocketIcon,
    UserRound,
    ZapIcon
} from "lucide-react";
import {
    contentVariants,
    footerItemVariants,
    iconVariants
} from "@/constants/animations.ts";
import { ImageConfig } from "@/constants";

const FOOTER_ICONS = [
    { text: 'Cost Effective', icon: DollarSignIcon },
    { text: 'Expert Assistance', icon: UserRound },
    { text: 'Speed & Efficiency', icon: ZapIcon },
    { text: 'Instant Deployment', icon: RocketIcon }
];

const AuthLayout = () => {
    return (
        <motion.div
            className="auth-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
        >
            {/* Left Side - Main Content */}
            <div className="auth-content">
                <div className="auth-content-wrapper">
                    <div className="auth-forms-wrapper pt-8">
                        <motion.div
                            className="logo-container"
                            variants={iconVariants}
                            initial="initial"
                            animate="animate"
                            layout
                            transition={{ type: "spring", stiffness: 100, damping: 15 }}
                        >
                            <a 
                                href="https://superengineer.ai" 
                                className="inline-block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm"
                                aria-label="SuperEngineer.ai - Go to homepage"
                            >
                                <ImageConfig.HeaderDarkLogo aria-hidden="true" />
                            </a>
                        </motion.div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key="auth-content"
                                className="auth-content-outlet"
                                variants={contentVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <Outlet />
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="privacy-policy-wrapper text-center text-sm text-secondary-foreground">
                        Your Privacy is our concern. Your data is safe with us.{' '}
                        <a 
                            href="https://www.superengineer.ai/privacy-policy" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-foreground underline font-medium hover:text-primary transition-colors"
                            aria-label="Privacy Policy - Opens in new tab"
                        >
                            Privacy Policy
                        </a>
                    </div>
                </div>
            </div>

            {/* Right Side - Testimonial Panel */}
            <motion.div
                className="auth-right-panel hide-in-small-screen bg-[var(--authLayoutRightBackgroundColor)]"
                variants={iconVariants}
                initial="initial"
                animate="animate"
            >
                <div className="auth-right-panel-content-wrapper">
                    <div className="auth-right-panel-content">
                        <motion.div
                            className="testimonial-title font-medium leading-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            What our <span className="highlight-primary-text">creators</span> said about us
                        </motion.div>

                        <motion.div
                            className="quote-image-wrapper"
                            variants={iconVariants}
                            initial="initial"
                            animate="animate"
                        >
                            <ImageConfig.QuoteImage />
                        </motion.div>

                        <motion.div
                            className="testimony-text-wrapper text-[18px]"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            “Super Engineer AI made my app development so easy — I turned my idea into a live app within just a few clicks!”
                        </motion.div>

                        <motion.div
                            className="testimony-author-text-wrapper"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <div className="testimony-author-name text-primary text-[18px]">Tausif Khan</div>
                            <div className="testimony-author-role text-sm">Product Manager at Cisco</div>
                        </motion.div>
                    </div>

                    <motion.div
                        className="auth-right-panel-footer"
                        variants={footerItemVariants}
                        initial="initial"
                        animate="animate"
                    >
                        {FOOTER_ICONS.map(({ text, icon: Icon }, index) => (
                            <motion.div key={index} className="footer-item-wrapper" variants={footerItemVariants}>
                                <div className="footer-item-icon">
                                    <Icon strokeWidth={1} size={16} className="text-primary" />
                                </div>
                                <div className="footer-item-text text-sm font-medium">{text}</div>
                            </motion.div>
                        )) as ReactNode}
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default AuthLayout;
